import { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";
import CorporateFareOutlinedIcon from "@mui/icons-material/CorporateFareOutlined";

import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Stack, Avatar } from "@mui/material";

import NewSidebar from "components/main/sidebar";
import PageLoader from "components/common/PageLoader";
import AppHeader from "components/main/navbar.component";
import AppHeaderNew from "components/main/newNavbar";

import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Button from "components/common/Button";
import CrossButtonIcon from "assets/svgs/CrossButtonIcon";
import BubbleTipIcon from "assets/svgs/BubbleTipIcon";
import avatarImage from "assets/images/fallback-image.svg";
import EdgeLogo from "components/Design/EdgeLogo/EdgeLogo";

import { getProfileImage, isEmptyObject, convertDate } from "components/Screens/CommonUtils";
import { checkIfPadding24Needed, getBackgroundColor, shouldAddCustomHeightOnCurrentPage } from "util/utils";
import { baseUrl, s3ImagesBaseURL } from "util/APIBaseUrl";

import { usePermissions } from "components/Hook";
import { useMenuItems, useStickyItems } from "components/main/sidebar/utils/AppRoutes";
import useMarkerWidget from "hooks/useMarkerWidget";

import { GetNoticeBoard } from "store/actions/DashBoard/GetNoticeBoard";
import { getEmployeeDetails } from "selectors/employee";
import { chatGetConversations as GetChatConversations } from "store/actions/Chat";

import { USER_TYPES } from "constants";
import NavigationRoutes from "constants/NavigationRoutes";

import "./main.component.css";

export default function MiniDrawer() {
  const dispatch = useDispatch();

  const { inAppNotifications, platformHeader, rmWidget, accountManagerSupport, markerIntegration } = useFlags();

  const [open, setOpen] = useState(true);
  const [openBubble, setOpenBubble] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { openMarkerWidget } = useMarkerWidget(markerIntegration);

  const [imageSrc, setImageSrc] = useState(avatarImage);
  const [closeSideBarFromMenuIcon, setCloseSideBarFromMenuIcon] = useState(false);
  const [copied, setCopied] = useState(false);

  const userAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.userAdditionalInfo);
  const loadingAdditionalInfo = useSelector((state) => state.UserAdditionalInfo.loadingAdditionalInfo);
  const employeeDetails = useSelector(getEmployeeDetails);

  const legacyRM = userAdditionalInfo?.relationshipManager ?? userAdditionalInfo?.accountManager;

  const { loggedInUser, loggedInUserType } = usePermissions();
  const { customerOnBoradingDate, userId, name, email } = loggedInUser;
  const { companyName, customerId, pendingCustomerId, pictureVersion, customerPictureVersion, startDate } =
    userAdditionalInfo;

  const getMenuItems = useMenuItems();
  const getStickyItems = useStickyItems();

  const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    minHeight: platformHeader ? "55px !important" : "46px !important",
  }));

  const getUserInfo = useCallback(() => {
    if (!userAdditionalInfo) return {};

    let startDateAsPayload;
    if (loggedInUserType === "customer") {
      startDateAsPayload = customerOnBoradingDate;
    } else if (loggedInUserType === "edge") {
      startDateAsPayload = startDate;
    } else {
      startDateAsPayload = startDate;
    }

    const isBenchedEmployee = loggedInUserType === "employee" && !(customerId || pendingCustomerId);
    const displayCompanyName = isBenchedEmployee ? "No company assigned" : companyName;

    const avatarUrl =
      loggedInUserType === "edge"
        ? `${baseUrl}/user/${userId}/logo?id=${pictureVersion}`
        : `${baseUrl}/customer/${customerId}/logo?id=${customerPictureVersion}`;

    return {
      name,
      companyName: displayCompanyName,
      email,
      startDate: convertDate(startDateAsPayload),
      isBenchedEmployee,
      avatar: isBenchedEmployee ? (
        <Avatar
          className="sidebar-user-avatar-img"
          sx={{
            backgroundColor: "var(--color-primitives-neutral-300) !important",
          }}
        >
          <CorporateFareOutlinedIcon />
        </Avatar>
      ) : (
        <Avatar
          alt={displayCompanyName || name}
          imgProps={{ crossOrigin: "anonymous" }}
          src={avatarUrl}
          className="sidebar-user-avatar-img"
          sx={{
            backgroundColor: "var(--color-primitives-primary-400) !important",
          }}
        />
      ),
    };
  }, [loggedInUser, userAdditionalInfo, loggedInUserType, customerId, pendingCustomerId]);

  const handleBubble = () => {
    setOpenBubble(!openBubble);
  };

  const copyToClipboard = (CopiedText) => {
    navigator.clipboard.writeText(CopiedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const getRmData = useCallback(() => {
    if (loggedInUserType === "employee") {
      const hrpoc = employeeDetails?.hrpoc;
      return {
        id: hrpoc?.userId || hrpoc?.Edge_Employee_ID,
        firstName: hrpoc?.firstName || hrpoc?.First_Name,
        email: hrpoc?.email || hrpoc?.Email,
        pictureVersion: hrpoc?.pictureVersion || hrpoc?.Picture_Version,
        picturePath: hrpoc?.picturePath || hrpoc?.Picture_Path,
        zoomLink: hrpoc?.zoomLink,
      };
    }
    if (loggedInUserType === "customer") {
      const edgeEmp = userAdditionalInfo?.edgeEmployee;
      const am = userAdditionalInfo?.relationshipManager ?? userAdditionalInfo?.accountManager;

      return {
        id: am?.userId || edgeEmp?.Edge_Employee_ID,
        firstName: am?.firstName || edgeEmp?.First_Name,
        email: am?.email || edgeEmp?.Email,
        pictureVersion: am?.pictureVersion || edgeEmp?.Picture_Version,
        picturePath: am?.picturePath || edgeEmp?.Picture_Path,
        zoomLink: am?.zoomLink || edgeEmp?.zoomLink,
      };
    }
    return {};
  }, [loggedInUserType, employeeDetails, userAdditionalInfo]);

  const rmData = getRmData();

  const handleScheduleCall = () => {
    if (rmData.zoomLink) {
      window.open(rmData.zoomLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleSidebarMenuItemClick = (path, openInNewTab) => {
    if (markerIntegration && path === NavigationRoutes.feedback) {
      openMarkerWidget();
      return;
    }
    if (openInNewTab) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else if (path) {
      navigate(path);
    }
  };

  useEffect(() => {
    if (rmWidget) {
      if (rmData.picturePath && rmData.id) {
        getProfileImage(rmData.id, rmData.pictureVersion, "user").then((img) => {
          setImageSrc(img);
        });
      } else {
        setImageSrc(avatarImage);
      }
    } else if ((userAdditionalInfo?.relationshipManager ?? userAdditionalInfo?.accountManager)?.picturePath) {
      const rm = userAdditionalInfo?.relationshipManager ?? userAdditionalInfo?.accountManager;
      getProfileImage(rm?.userId, rm?.pictureVersion, "user").then((img) => {
        setImageSrc(img);
      });
    } else {
      setImageSrc(avatarImage);
    }
  }, [rmWidget, rmData.id, rmData.picturePath, rmData.pictureVersion, userAdditionalInfo]);

  useEffect(() => {
    if (isEmptyObject(loggedInUser) && loggedInUserType !== "employee") {
      const req = {
        edge: "customers/noticeboard",
        customer: `customer/${loggedInUser?.customerId}/noticeboard`,
      };

      dispatch(
        GetNoticeBoard({
          request: req[loggedInUserType],
        })
      );
    }
  }, [loggedInUser, loggedInUserType]);

  useEffect(() => {
    if (window.innerWidth < 1280) setOpen(false);
  }, []);

  useEffect(() => {
    if (loggedInUserType === "employee" || loggedInUserType === "customer" || loggedInUserType === "edge") {
      dispatch(GetChatConversations());
    }
  }, [dispatch, loggedInUserType]);

  useEffect(() => {
    if (location?.pathname === "/main/community") setOpen(false);
    if (window.innerWidth > 1280 && location?.pathname !== "/main/community" && open == false)
      if (closeSideBarFromMenuIcon !== true) setOpen(true);
      else setOpen(false);
  }, [location]);

  return (
    <>
      {loadingAdditionalInfo && <PageLoader />}
      <div style={{ display: "flex", overflowX: "hidden", width: "100%" }} className="disable-scrollbars">
        <CssBaseline />

        {inAppNotifications && loggedInUserType !== USER_TYPES.EDGE ? (
          <AppHeaderNew
            open={open}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            setOpen={setOpen}
            userDetails={loggedInUser}
            userAdditionalInfo={userAdditionalInfo}
            setCloseSideBarFromMenuIcon={setCloseSideBarFromMenuIcon}
          />
        ) : (
          <AppHeader
            open={open}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            setOpen={setOpen}
            userDetails={loggedInUser}
            userAdditionalInfo={userAdditionalInfo}
            setCloseSideBarFromMenuIcon={setCloseSideBarFromMenuIcon}
          />
        )}

        <NewSidebar
          open={open}
          menuItems={getMenuItems()}
          onToggle={setOpen}
          logo={<EdgeLogo size="md" state="light" />}
          smallLogo={<EdgeLogo size="md" icon state="light" />}
          userInfo={getUserInfo()}
          stickyItems={getStickyItems()}
          onMenuItemClick={handleSidebarMenuItemClick}
        />

        <Box
          component="main"
          className="main-box"
          sx={{
            flexGrow: 1,
            p: loggedInUserType === "edge" && checkIfPadding24Needed() ? "24px !important" : "0px !important",
            backgroundColor: getBackgroundColor(),
          }}
        >
          {location?.pathname !== "/main/community" ? <DrawerHeader /> : null}
          {loggedInUserType === "edge" ? (
            <Stack
              className="main-content-stack"
              sx={{
                maxHeight: shouldAddCustomHeightOnCurrentPage() ? "calc(100vh - 95px)" : "auto",
                overflow: "auto",
              }}
            >
              <Outlet />
            </Stack>
          ) : (
            <Outlet />
          )}
          {rmWidget
            ? (loggedInUserType === "customer" || loggedInUserType === "employee") && (
                <Box className="rm-widget">
                  <Box
                    className="rm-widget-trigger"
                    onClick={handleBubble}
                    component="img"
                    src={imageSrc[rmData.id] ? imageSrc[rmData.id] : `${s3ImagesBaseURL}/placeholder-avatar.svg`}
                    alt="Relationship Manager"
                  />

                  {!openBubble && (
                    <Box className="rm-widget-card">
                      <div className="rm-widget-close" onClick={handleBubble}>
                        <CloseIcon sx={{ fontSize: 20, color: "#999" }} />
                      </div>

                      {rmData.id ? (
                        <>
                          <div className="rm-widget-avatar">
                            <Box
                              component="img"
                              src={
                                imageSrc[rmData.id] ? imageSrc[rmData.id] : `${s3ImagesBaseURL}/placeholder-avatar.svg`
                              }
                              alt={rmData.firstName}
                            />
                          </div>

                          <div className="rm-widget-name heading-h4-semibold color-text-headings-primary">
                            Hi, I&apos;m {rmData.firstName}!
                          </div>

                          <div className="rm-widget-description para-body-s-regular color-text-body-primary">
                            I&apos;m your{" "}
                            <span className="para-body-s-semibold color-text-headings-primary">
                              Relationship Manager
                            </span>
                            &mdash;happy to help with finance, HR, or any general requests or concerns you may have.
                          </div>
                          <div className="w-100p">
                            {" "}
                            <Button
                              isFullWidth
                              size="medium"
                              variant="contained"
                              color="primary"
                              label="Schedule a call"
                              startIcon={<PhoneOutlinedIcon sx={{ fontSize: 20 }} />}
                              onClick={handleScheduleCall}
                            />
                          </div>

                          {rmData.email && (
                            <div className="rm-widget-email">
                              <span className="rm-widget-email-label para-body-xs-regular">or email me directly</span>
                              <div className="rm-widget-email-row">
                                <span className="rm-widget-email-value para-body-s-regular">{rmData.email}</span>
                                <button
                                  type="button"
                                  className={`rm-widget-copy-btn ${copied ? "rm-widget-copy-btn--copied" : ""}`}
                                  onClick={() => copyToClipboard(rmData.email)}
                                >
                                  <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
                                  {copied ? "Copied!" : "Copy"}
                                </button>
                              </div>
                            </div>
                          )}

                          {loggedInUserType === "employee" && (
                            <div className="rm-widget-hr-text para-body-xs-regular color-text-body-primary">
                              Observed serious issues that you want to raise for HR? Please reach out at{" "}
                              <span className="rm-widget-hr-email">ethicspoint@onedge.co</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="rm-widget-description" style={{ marginBottom: 0 }}>
                          Hi! You will be assigned with your Relationship Manager shortly.
                        </p>
                      )}
                    </Box>
                  )}
                </Box>
              )
            : loggedInUserType === "customer" && (
                <Box className="bubble-popup">
                  <Box
                    onClick={handleBubble}
                    component="img"
                    src={
                      imageSrc[legacyRM?.userId]
                        ? imageSrc[legacyRM?.userId]
                        : `${s3ImagesBaseURL}/placeholder-avatar.svg`
                    }
                    width="70px"
                    height="70px"
                    sx={{ width: "70px", height: "70px" }}
                  />

                  {!openBubble && (
                    <Box className="bubble-content">
                      {legacyRM?.userId ? (
                        <>
                          <p className="hand-wave">👋</p>
                          <div className="cross-button" onClick={handleBubble}>
                            <CrossButtonIcon />
                          </div>
                          <p>
                            Hello, I am <strong>{legacyRM?.firstName} !</strong> your new
                            {accountManagerSupport ? " Relationship Manager" : " Account Manager"}. Contact me in case
                            you need any help!
                          </p>
                          <div className="contact">
                            <span>Email</span>
                            <div>
                              <p>{legacyRM?.email}</p>
                              <button type="button" onClick={() => copyToClipboard(legacyRM?.email)}>
                                Copy
                              </button>
                            </div>
                          </div>
                          <div className="contact">
                            <span>Phone number</span>
                            <div>
                              <p>{legacyRM?.phoneNumber}</p>
                              <button type="button" onClick={() => copyToClipboard(legacyRM?.phoneNumber)}>
                                Copy
                              </button>
                            </div>
                          </div>
                          <div className="tip-down">
                            <BubbleTipIcon />
                          </div>
                        </>
                      ) : (
                        <p style={{ marginBottom: "0" }}>Hi! You will be assigned with your account manager shortly.</p>
                      )}
                    </Box>
                  )}
                </Box>
              )}
        </Box>
      </div>
    </>
  );
}
